import React, { useState, useEffect } from 'react';
import {
    Modal,
    Form,
    Input,
    InputNumber,
    DatePicker,
    TimePicker,
    Select,
    Button,
    Row,
    Col,
    Card,
    Typography,
    Divider,
    Space,
    message,
    Table,
    Tag,
    Descriptions,
    Alert,
    Tabs,
    Badge,
    Tooltip
} from 'antd';
import {
    EditOutlined,
    CalculatorOutlined,
    SaveOutlined,
    SendOutlined,
    DollarOutlined,
    PercentageOutlined,
    ShoppingCartOutlined,
    UserOutlined,
    CalendarOutlined,
    PhoneOutlined,
    MailOutlined,
    EnvironmentOutlined,
    TeamOutlined,
    ClockCircleOutlined,
    FileTextOutlined,
    GiftOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

function EnhancedLeadReviewModal({ visible, lead, onClose, onSave, onSendQuote }) {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    
    // Helper function to safely format numbers
    const safeFormatNumber = (value, decimals = 0) => {
        if (typeof value === 'number' && !isNaN(value)) {
            return decimals > 0 ? value.toFixed(decimals) : value.toString();
        }
        return decimals > 0 ? '0.0' : '0';
    };
    const [calculations, setCalculations] = useState({
        baseAmount: 0,
        perPersonRate: 0,
        totalGuests: 0,
        subtotal: 0,
        tax: 0,
        discount: 0,
        discountPercentage: 0,
        total: 0,
        serviceFees: 0,
        basePricePerPerson: 0,
        taxableAmount: 0,
        autoCalculated: false
    });
    const [selectedDishes, setSelectedDishes] = useState([]);
    const [availableDishes, setAvailableDishes] = useState([]);
    const [extraServices, setExtraServices] = useState([]);
    const [activeTab, setActiveTab] = useState('overview');

    useEffect(() => {
        if (lead && visible) {
            // Initialize form with lead data
            form.setFieldsValue({
                event_type: lead.event_type,
                event_date: lead.event_date ? dayjs(lead.event_date) : null,
                event_time: lead.event_time ? dayjs(lead.event_time, 'HH:mm') : null,
                venue: lead.venue,
                guest_count: lead.guest_count,
                special_requirements: lead.special_requirements,
                estimated_total: lead.estimated_total,
                final_total: lead.final_total,
                vendor_notes: lead.vendor_notes,
                customer_notes: lead.customer_notes,
                status: lead.status
            });

            // Initialize calculations
            const guestCount = parseInt(lead.guest_count) || 0;
            const baseAmount = parseFloat(lead.estimated_total) || 0;
            const perPersonRate = guestCount > 0 ? baseAmount / guestCount : 0;
            const basePricePerPerson = parseFloat(lead.base_price_per_person) || 0;
            const serviceFees = parseFloat(lead.service_fees) || 0;
            const discountPercentage = parseFloat(lead.discount_percentage) || 0;
            const discountAmount = parseFloat(lead.discount_amount) || 0;
            const taxAmount = parseFloat(lead.tax_amount) || 0;
            const finalTotal = parseFloat(lead.final_total) || 0;
            
            setCalculations({
                baseAmount,
                perPersonRate,
                totalGuests: guestCount,
                subtotal: baseAmount,
                tax: taxAmount,
                discount: discountAmount,
                discountPercentage: discountPercentage,
                total: finalTotal,
                serviceFees: serviceFees,
                basePricePerPerson: basePricePerPerson,
                taxableAmount: baseAmount - discountAmount,
                autoCalculated: lead.auto_calculated_quantities ? true : false
            });

            // Load selected dishes
            setSelectedDishes(lead.selected_dishes || []);
            
            // Load available dishes
            loadAvailableDishes();
            
            // Load extra services
            loadExtraServices();
        }
    }, [lead, visible, form]);

    const loadAvailableDishes = async () => {
        try {
            const response = await api.getMenus({ per_page: 100 });
            if (response.success) {
                setAvailableDishes(response.data?.data || response.data || []);
            }
        } catch (error) {
            console.error('Failed to load dishes:', error);
        }
    };

    const loadExtraServices = async () => {
        // Mock extra services - in real app, this would come from API
        setExtraServices([
            { id: 1, name: 'Decoration', price: 5000, description: 'Basic decoration setup' },
            { id: 2, name: 'Photography', price: 8000, description: 'Event photography service' },
            { id: 3, name: 'Music System', price: 3000, description: 'DJ and sound system' },
            { id: 4, name: 'Transportation', price: 2000, description: 'Pickup and drop service' },
            { id: 5, name: 'Catering Staff', price: 1500, description: 'Additional serving staff' }
        ]);
    };

    const handleCalculationChange = (field, value) => {
        setCalculations(prev => {
            // Ensure value is a number
            const numericValue = parseFloat(value) || 0;
            const newCalc = { ...prev, [field]: numericValue };
            
            // Recalculate based on the change
            if (field === 'discountPercentage') {
                newCalc.discount = (newCalc.subtotal * numericValue) / 100;
            } else if (field === 'discount') {
                newCalc.discountPercentage = newCalc.subtotal > 0 ? (numericValue / newCalc.subtotal) * 100 : 0;
            }
            
            newCalc.total = newCalc.subtotal + newCalc.tax - newCalc.discount;
            
            return newCalc;
        });
    };

    const addDish = (dish) => {
        const existingDish = selectedDishes.find(d => d.id === dish.id);
        if (existingDish) {
            setSelectedDishes(prev => 
                prev.map(d => 
                    d.id === dish.id 
                        ? { ...d, quantity: d.quantity + 1 }
                        : d
                )
            );
        } else {
            setSelectedDishes(prev => [...prev, { ...dish, quantity: 1 }]);
        }
        
        // Recalculate total
        recalculateTotal();
    };

    const removeDish = (dishId) => {
        setSelectedDishes(prev => prev.filter(d => d.id !== dishId));
        recalculateTotal();
    };

    const updateDishQuantity = (dishId, quantity) => {
        if (quantity <= 0) {
            removeDish(dishId);
            return;
        }
        
        setSelectedDishes(prev => 
            prev.map(d => 
                d.id === dishId 
                    ? { ...d, quantity }
                    : d
            )
        );
        recalculateTotal();
    };

    const recalculateTotal = () => {
        const dishesTotal = selectedDishes.reduce((sum, dish) => 
            sum + (parseFloat(dish.price) * parseInt(dish.quantity)), 0
        );
        
        setCalculations(prev => {
            const subtotal = dishesTotal + parseFloat(prev.serviceFees) + (parseFloat(prev.basePricePerPerson) * parseInt(prev.totalGuests));
            const discountAmount = (subtotal * parseFloat(prev.discountPercentage)) / 100;
            const taxableAmount = subtotal - discountAmount;
            const taxAmount = taxableAmount * 0.18;
            const total = taxableAmount + taxAmount;
            
            const newCalc = {
                ...prev,
                subtotal: subtotal,
                tax: taxAmount,
                discount: discountAmount,
                taxableAmount: taxableAmount,
                total: total
            };
            return newCalc;
        });
    };

    const calculateAutoQuantities = async () => {
        try {
            setLoading(true);
            
            const calculationData = {
                base_price_per_person: calculations.basePricePerPerson,
                selected_dishes: selectedDishes,
                extra_services: extraServices.filter(s => s.selected),
                discount_percentage: calculations.discountPercentage,
                service_fees: calculations.serviceFees
            };

            const response = await api.calculateAutoQuantities(lead.id, calculationData);
            
            if (response.success) {
                const calcData = response.data.calculations;
                
                // Update calculations with auto-calculated values
                setCalculations(prev => ({
                    ...prev,
                    subtotal: parseFloat(calcData.subtotal) || 0,
                    tax: parseFloat(calcData.tax_amount) || 0,
                    discount: parseFloat(calcData.discount_amount) || 0,
                    taxableAmount: parseFloat(calcData.taxable_amount) || 0,
                    total: parseFloat(calcData.final_total) || 0,
                    autoCalculated: true
                }));

                // Update selected dishes with auto-calculated quantities
                if (calcData.auto_calculated_quantities) {
                    setSelectedDishes(calcData.auto_calculated_quantities.map(item => ({
                        id: item.dish_id,
                        name: item.name,
                        price: item.price,
                        quantity: item.quantity,
                        total_price: item.total_price
                    })));
                }

                message.success('Auto quantities calculated successfully!');
            } else {
                message.error(response.message || 'Failed to calculate auto quantities');
            }
        } catch (error) {
            console.error('Auto calculation error:', error);
            message.error('Failed to calculate auto quantities');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();
            
            const updatedLead = {
                ...lead,
                ...values,
                event_date: values.event_date?.format('YYYY-MM-DD'),
                event_time: values.event_time?.format('HH:mm'),
                selected_dishes: selectedDishes,
                estimated_total: calculations.subtotal,
                final_total: calculations.total,
                vendor_notes: values.vendor_notes,
                discount_percentage: calculations.discountPercentage,
                discount_amount: calculations.discount
            };

            await api.updateLead(lead.id, updatedLead);
            message.success('Lead updated successfully');
            onSave(updatedLead);
        } catch (error) {
            message.error('Failed to update lead');
        } finally {
            setLoading(false);
        }
    };

    const handleSendQuote = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();
            
            const quoteData = {
                lead_id: lead.id,
                customer_id: lead.user_id,
                event_details: {
                    event_type: values.event_type || lead.event_type,
                    date: values.event_date?.format('YYYY-MM-DD') || lead.event_date,
                    time: values.event_time?.format('HH:mm') || lead.event_time,
                    venue: values.venue || lead.venue,
                    occasion: values.event_type || lead.event_type,
                    guest_count: values.guest_count || lead.guest_count
                },
                service_requirements: {
                    special_requirements: values.special_requirements || lead.special_requirements,
                    selected_dishes: selectedDishes,
                    extra_services: extraServices.filter(s => s.selected),
                    auto_calculated: calculations.autoCalculated
                },
                packages: selectedDishes.map(dish => ({
                    id: dish.id || Math.random().toString(36).substr(2, 9),
                    name: dish.name,
                    description: dish.description || '',
                    price: dish.price,
                    quantity: dish.quantity
                })),
                subtotal: calculations.subtotal,
                service_cost: calculations.serviceFees,
                tax_amount: calculations.tax,
                total_amount: calculations.total,
                special_instructions: values.vendor_notes,
                discount_percentage: calculations.discountPercentage,
                discount_amount: calculations.discount,
                base_price_per_person: calculations.basePricePerPerson,
                auto_calculated_quantities: calculations.autoCalculated ? selectedDishes : null
            };

            console.log('Sending quote data:', quoteData);

            const response = await api.createQuote(quoteData);
            
            console.log('Quote response:', response);
            
            if (response.success) {
                // Update lead status to quoted
                await api.updateLead(lead.id, { 
                    status: 'quoted',
                    final_total: calculations.total,
                    discount_percentage: calculations.discountPercentage,
                    discount_amount: calculations.discount,
                    vendor_notes: values.vendor_notes
                });
                
                message.success('Quote sent successfully! Customer will be notified.');
                onSendQuote(response.data);
            } else {
                console.error('Quote creation failed:', response);
                message.error(response.message || 'Failed to send quote');
            }
        } catch (error) {
            console.error('Send quote error:', error);
            message.error('Failed to send quote');
        } finally {
            setLoading(false);
        }
    };

    const dishColumns = [
        {
            title: 'Dish',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Price',
            dataIndex: 'price',
            key: 'price',
            render: (price) => `₹${price?.toLocaleString() || 0}`
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            render: (quantity, record) => (
                <InputNumber
                    min={1}
                    value={quantity}
                    onChange={(value) => updateDishQuantity(record.id, value)}
                    style={{ width: 80 }}
                />
            )
        },
        {
            title: 'Total',
            key: 'total',
            render: (_, record) => `₹${(record.price * record.quantity)?.toLocaleString() || 0}`
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Button 
                    type="link" 
                    danger 
                    onClick={() => removeDish(record.id)}
                >
                    Remove
                </Button>
            )
        }
    ];

    const extraServiceColumns = [
        {
            title: 'Service',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'Price',
            dataIndex: 'price',
            key: 'price',
            render: (price) => `₹${price?.toLocaleString() || 0}`
        },
        {
            title: 'Add',
            key: 'add',
            render: (_, record) => (
                <Button 
                    type="primary" 
                    size="small"
                    onClick={() => {
                        setExtraServices(prev => 
                            prev.map(s => 
                                s.id === record.id 
                                    ? { ...s, selected: !s.selected }
                                    : s
                            )
                        );
                    }}
                >
                    {record.selected ? 'Remove' : 'Add'}
                </Button>
            )
        }
    ];

    const availableDishColumns = [
        {
            title: 'Dish',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            render: (category) => category?.name || 'N/A'
        },
        {
            title: 'Price',
            dataIndex: 'price',
            key: 'price',
            render: (price) => `₹${price?.toLocaleString() || 0}`
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Button 
                    type="primary" 
                    size="small"
                    onClick={() => addDish(record)}
                >
                    Add
                </Button>
            )
        }
    ];

    if (!lead) return null;

    return (
        <Modal
            title={
                <div className="flex items-center justify-between">
                    <span>Lead Review & Quote Creation</span>
                    <Badge 
                        count={selectedDishes.length} 
                        style={{ backgroundColor: '#52c41a' }}
                    >
                        <ShoppingCartOutlined />
                    </Badge>
                </div>
            }
            open={visible}
            onCancel={onClose}
            width={1200}
            footer={[
                <Button key="close" onClick={onClose}>
                    Close
                </Button>,
                <Button 
                    key="save" 
                    type="default" 
                    icon={<SaveOutlined />}
                    onClick={handleSave}
                    loading={loading}
                >
                    Save Changes
                </Button>,
                <Button 
                    key="quote" 
                    type="primary" 
                    icon={<SendOutlined />}
                    onClick={handleSendQuote}
                    loading={loading}
                >
                    Send Quote
                </Button>
            ]}
        >
            <Tabs 
                activeKey={activeTab} 
                onChange={setActiveTab}
                items={[
                    {
                        key: 'overview',
                        label: 'Overview',
                        children: (
                            <div className="space-y-6">
                                {/* Customer Information */}
                                <Card title="Customer Information" size="small">
                                    <Descriptions column={2} size="small">
                                        <Descriptions.Item label={<><UserOutlined /> Customer</>}>
                                            {lead.user?.name}
                                        </Descriptions.Item>
                                        <Descriptions.Item label={<><MailOutlined /> Email</>}>
                                            {lead.user?.email}
                                        </Descriptions.Item>
                                        <Descriptions.Item label={<><PhoneOutlined /> Phone</>}>
                                            {lead.contact_phone}
                                        </Descriptions.Item>
                                        <Descriptions.Item label={<><EnvironmentOutlined /> Venue</>}>
                                            {lead.venue}
                                        </Descriptions.Item>
                                        <Descriptions.Item label={<><CalendarOutlined /> Event Date</>}>
                                            {lead.event_date}
                                        </Descriptions.Item>
                                        <Descriptions.Item label={<><ClockCircleOutlined /> Event Time</>}>
                                            {lead.event_time}
                                        </Descriptions.Item>
                                        <Descriptions.Item label={<><TeamOutlined /> Guest Count</>}>
                                            {lead.guest_count} people
                                        </Descriptions.Item>
                                        <Descriptions.Item label={<><FileTextOutlined /> Event Type</>}>
                                            {lead.event_type}
                                        </Descriptions.Item>
                                    </Descriptions>
                                </Card>

                                {/* Event Details Form */}
                                <Card title="Event Details" size="small">
                                    <Form form={form} layout="vertical">
                                        <Row gutter={16}>
                                            <Col span={8}>
                                                <Form.Item name="event_type" label="Event Type">
                                                    <Input />
                                                </Form.Item>
                                            </Col>
                                            <Col span={8}>
                                                <Form.Item name="event_date" label="Event Date">
                                                    <DatePicker style={{ width: '100%' }} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={8}>
                                                <Form.Item name="event_time" label="Event Time">
                                                    <TimePicker style={{ width: '100%' }} format="HH:mm" />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <Form.Item name="venue" label="Venue">
                                                    <Input />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item name="guest_count" label="Guest Count">
                                                    <InputNumber style={{ width: '100%' }} min={1} />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Form.Item name="special_requirements" label="Special Requirements">
                                            <TextArea rows={3} />
                                        </Form.Item>
                                    </Form>
                                </Card>
                            </div>
                        )
                    },
                    {
                        key: 'menu',
                        label: 'Menu Selection',
                        children: (
                            <div className="space-y-6">
                                <Row gutter={[16, 16]}>
                                    <Col span={12}>
                                        <Card title="Selected Dishes" size="small">
                                            <Table
                                                columns={dishColumns}
                                                dataSource={selectedDishes}
                                                pagination={false}
                                                size="small"
                                                rowKey="id"
                                            />
                                        </Card>
                                    </Col>
                                    <Col span={12}>
                                        <Card title="Available Dishes" size="small">
                                            <Table
                                                columns={availableDishColumns}
                                                dataSource={availableDishes}
                                                pagination={{ pageSize: 5 }}
                                                size="small"
                                                rowKey="id"
                                            />
                                        </Card>
                                    </Col>
                                </Row>
                            </div>
                        )
                    },
                    {
                        key: 'services',
                        label: 'Extra Services',
                        children: (
                            <Card title="Additional Services" size="small">
                                <Table
                                    columns={extraServiceColumns}
                                    dataSource={extraServices}
                                    pagination={false}
                                    size="small"
                                    rowKey="id"
                                />
                            </Card>
                        )
                    },
                    {
                        key: 'pricing',
                        label: 'Pricing & Discounts',
                        children: (
                            <div className="space-y-6">
                                <Card title="Price Calculation" size="small">
                                    <Row gutter={[16, 16]}>
                                        <Col span={12}>
                                            <div className="space-y-4">
                                                <div className="flex justify-between">
                                                    <Text>Base Price ({calculations.totalGuests} guests):</Text>
                                                    <Text>₹{(calculations.basePricePerPerson * calculations.totalGuests)?.toLocaleString()}</Text>
                                                </div>
                                                <div className="flex justify-between">
                                                    <Text>Dishes Total:</Text>
                                                    <Text>₹{(calculations.subtotal - calculations.serviceFees - (calculations.basePricePerPerson * calculations.totalGuests))?.toLocaleString()}</Text>
                                                </div>
                                                <div className="flex justify-between">
                                                    <Text>Service Fees:</Text>
                                                    <Text>₹{calculations.serviceFees?.toLocaleString()}</Text>
                                                </div>
                                                <div className="flex justify-between">
                                                    <Text strong>Subtotal:</Text>
                                                    <Text strong>₹{calculations.subtotal?.toLocaleString()}</Text>
                                                </div>
                                                <div className="flex justify-between">
                                                    <Text>Discount ({safeFormatNumber(calculations.discountPercentage, 1)}%):</Text>
                                                    <Text className="text-red-600">-₹{calculations.discount?.toLocaleString()}</Text>
                                                </div>
                                                <div className="flex justify-between">
                                                    <Text>Taxable Amount:</Text>
                                                    <Text>₹{calculations.taxableAmount?.toLocaleString()}</Text>
                                                </div>
                                                <div className="flex justify-between">
                                                    <Text>Tax (18%):</Text>
                                                    <Text>₹{calculations.tax?.toLocaleString()}</Text>
                                                </div>
                                                <Divider />
                                                <div className="flex justify-between">
                                                    <Text strong className="text-lg">Total:</Text>
                                                    <Text strong className="text-lg text-orange-600">
                                                        ₹{calculations.total?.toLocaleString()}
                                                    </Text>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col span={12}>
                                            <div className="space-y-4">
                                                <div>
                                                    <Text strong>Base Price Per Person</Text>
                                                    <InputNumber
                                                        placeholder="Price per person"
                                                        value={calculations.basePricePerPerson}
                                                        onChange={(value) => {
                                                            setCalculations(prev => ({ ...prev, basePricePerPerson: value || 0 }));
                                                            recalculateTotal();
                                                        }}
                                                        prefix="₹"
                                                        style={{ width: '100%' }}
                                                        min={0}
                                                    />
                                                </div>
                                                <div>
                                                    <Text strong>Service Fees</Text>
                                                    <InputNumber
                                                        placeholder="Additional service fees"
                                                        value={calculations.serviceFees}
                                                        onChange={(value) => {
                                                            setCalculations(prev => ({ ...prev, serviceFees: value || 0 }));
                                                            recalculateTotal();
                                                        }}
                                                        prefix="₹"
                                                        style={{ width: '100%' }}
                                                        min={0}
                                                    />
                                                </div>
                                                <div>
                                                    <Text strong>Apply Discount</Text>
                                                    <div className="flex space-x-2 mt-2">
                                                        <InputNumber
                                                            placeholder="Percentage"
                                                            value={calculations.discountPercentage}
                                                            onChange={(value) => handleCalculationChange('discountPercentage', value || 0)}
                                                            suffix="%"
                                                            style={{ width: 120 }}
                                                            min={0}
                                                            max={100}
                                                        />
                                                        <InputNumber
                                                            placeholder="Amount"
                                                            value={calculations.discount}
                                                            onChange={(value) => handleCalculationChange('discount', value || 0)}
                                                            prefix="₹"
                                                            style={{ width: 120 }}
                                                            min={0}
                                                        />
                                                    </div>
                                                </div>
                                                <Button 
                                                    type="primary" 
                                                    icon={<CalculatorOutlined />}
                                                    onClick={calculateAutoQuantities}
                                                    loading={loading}
                                                    style={{ width: '100%' }}
                                                >
                                                    Auto Calculate Quantities
                                                </Button>
                                                {calculations.autoCalculated && (
                                                    <Alert
                                                        message="Quantities auto-calculated based on guest count"
                                                        type="success"
                                                        showIcon
                                                        icon={<CalculatorOutlined />}
                                                    />
                                                )}
                                            </div>
                                        </Col>
                                    </Row>
                                </Card>

                                <Card title="Vendor Notes" size="small">
                                    <Form.Item name="vendor_notes" label="Notes for Customer">
                                        <TextArea rows={4} placeholder="Add any special notes or instructions for the customer..." />
                                    </Form.Item>
                                </Card>
                            </div>
                        )
                    }
                ]}
            />
        </Modal>
    );
}

export default EnhancedLeadReviewModal;
